import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { BigCommerceIntegrationFactory } from '../../services/integrations/ecommerce/BigCommerceIntegrationFactory';
import { SelectField } from "components/ui/select-field";

interface BigCommerceIntegrationSetupProps {
  onIntegrationCreated: (integration: any) => void;
  onCancel: () => void;
}

export const BigCommerceIntegrationSetup: React.FC<BigCommerceIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  
  const [formData, setFormData] = useState({
    storeHash: '',
    accessToken: '',
    clientId: '',
    clientSecret: '',
    apiVersion: 'v3' as 'v2' | 'v3',
    webhookSecret: '',
    enableWebhooks: false
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.storeHash) {
      setError('Store hash is required');
      return false;
    }
    if (!/^[a-z0-9]{10}$/.test(formData.storeHash)) {
      setError('Store hash must be a 10-character alphanumeric string');
      return false;
    }
    if (!formData.accessToken) {
      setError('Access token is required');
      return false;
    }
    if (formData.accessToken.length < 20) {
      setError('Access token appears to be invalid (too short)');
      return false;
    }
    return true;
  };

  const testConnection = async () => {
    if (!validateStep1()) return;

    setLoading(true);
    setError('');
    setTestResult(null);

    try {
      const result = await BigCommerceIntegrationFactory.testConnection(formData);
      setTestResult(result);
      
      if (result.success) {
        setStep(2);
      } else {
        setError(result.error || 'Connection test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const createIntegration = async () => {
    setLoading(true);
    setError('');

    try {
      const integration = BigCommerceIntegrationFactory.create(formData);
      
      // Test the integration
      const connectionResult = await integration.connect();
      if (!connectionResult.success) {
        throw new Error(connectionResult.message);
      }

      onIntegrationCreated(integration);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">BigCommerce Store Connection</h3>
        <p className="text-content-secondary">
          Connect your BigCommerce store to sync products, customers, and orders with your email marketing platform.
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Before you start</h4>
            <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
              You'll need to create an API account in your BigCommerce store. Go to{' '}
              <strong>Advanced Settings → API Accounts</strong> and create a new API account with the following scopes:
            </p>
            <ul className="text-blue-800 dark:text-blue-300 text-sm mt-2 list-disc list-inside">
              <li>Products: Read & Write</li>
              <li>Customers: Read & Write</li>
              <li>Orders: Read</li>
              <li>Information & Settings: Read</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Store Hash *
          </label>
          <input
            type="text"
            value={formData.storeHash}
            onChange={(e) => handleInputChange('storeHash', e.target.value)}
            placeholder="abc1234567"
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-content-muted mt-1">
            Found in your store's API path: /stores/<strong>abc1234567</strong>/v3/
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Version
          </label>
          <SelectField
            value={formData.apiVersion}
            onChange={(e) => handleInputChange('apiVersion', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="v3">V3 (Recommended)</option>
            <option value="v2">V2 (Legacy)</option>
          </SelectField>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Access Token *
        </label>
        <div className="relative">
          <input
            type={showAccessToken ? 'text' : 'password'}
            value={formData.accessToken}
            onChange={(e) => handleInputChange('accessToken', e.target.value)}
            placeholder="Enter your BigCommerce access token"
            className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={() => setShowAccessToken(!showAccessToken)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-subtle hover:text-content-secondary"
          >
            {showAccessToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-content-muted mt-1">
          The access token from your BigCommerce API account
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client ID (Optional)
          </label>
          <input
            type="text"
            value={formData.clientId}
            onChange={(e) => handleInputChange('clientId', e.target.value)}
            placeholder="Enter client ID"
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client Secret (Optional)
          </label>
          <div className="relative">
            <input
              type={showClientSecret ? 'text' : 'password'}
              value={formData.clientSecret}
              onChange={(e) => handleInputChange('clientSecret', e.target.value)}
              placeholder="Enter client secret"
              className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowClientSecret(!showClientSecret)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-subtle hover:text-content-secondary"
            >
              {showClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Cancel
        </button>
        <button
          onClick={testConnection}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Test Connection
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Connection Successful!</h3>
        <p className="text-content-secondary">
          Configure additional settings for your BigCommerce integration.
        </p>
      </div>

      {testResult && testResult.success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
            <p className="text-green-800 dark:text-green-300 font-medium">Connected to BigCommerce Store</p>
          </div>
          {testResult.storeInfo && (
            <div className="text-sm text-green-700 dark:text-green-300">
              <p><strong>Store:</strong> {testResult.storeInfo.name || 'BigCommerce Store'}</p>
              {testResult.storeInfo.domain && (
                <p><strong>Domain:</strong> {testResult.storeInfo.domain}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 border border-line rounded-lg">
          <div>
            <h4 className="font-medium text-content">Enable Webhooks</h4>
            <p className="text-sm text-content-secondary">
              Get real-time updates when products, customers, or orders change
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableWebhooks}
              onChange={(e) => handleInputChange('enableWebhooks', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-dark-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after: after:border-line-strong after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {formData.enableWebhooks && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Webhook Secret
            </label>
            <input
              type="text"
              value={formData.webhookSecret}
              onChange={(e) => handleInputChange('webhookSecret', e.target.value)}
              placeholder="Enter a secure webhook secret (min 16 characters)"
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-content-muted mt-1">
              Used to verify webhook authenticity. Generate a random string of at least 16 characters.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => setStep(1)}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Back
        </button>
        <button
          onClick={createIntegration}
          disabled={loading}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Create Integration
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-content">BigCommerce Integration Setup</h2>
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              1
            </span>
            <span className="w-8 h-px bg-gray-300"></span>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              2
            </span>
          </div>
        </div>
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
};